import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { Check, Key, Loader2, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
  // Search API Providers
  {
    value: "dataforseo",
    label: "DataForSEO",
    description: "主要 Trends/PAA/AIO API（使用 Basic Auth）",
    category: "search",
    authType: "basic" as const,
  },
  {
    value: "valueserp",
    label: "ValueSERP",
    description: "Google Trends API（含 WoW 與 YoY 計算）",
    category: "search",
  },
  {
    value: "serpapi",
    label: "SerpAPI（備援）",
    description: "備援使用，或用於 AI Overview / 其他資料源",
    category: "search",
  },
  {
    value: "zenserp",
    label: "ZenSERP（備援）",
    description: "第二備援 Google Trends API",
    category: "search",
  },
  // AI Providers
  {
    value: "openai",
    label: "OpenAI",
    description: "GPT-5.1 Thinking 用於深度策略分析",
    category: "ai",
  },
  {
    value: "gemini",
    label: "Google Gemini",
    description: "Gemini 3 Pro 用於 AI Overview 模擬",
    category: "ai",
  },
  {
    value: "claude",
    label: "Anthropic Claude",
    description: "Claude 用於內容分析備援",
    category: "ai",
  },
  {
    value: "grok",
    label: "xAI Grok",
    description: "Grok 用於內容分析備援",
    category: "ai",
  },
  {
    value: "deepseek",
    label: "DeepSeek",
    description: "DeepSeek 用於內容分析備援",
    category: "ai",
  },
];

// 支援免費/付費選項的服務類型
const SERVICES_WITH_FREE_TIER = ["gemini", "valueserp", "serpapi", "zenserp"];

// 使用 Basic Auth 的服務類型
const SERVICES_WITH_BASIC_AUTH = ["dataforseo"];

export default function ApiKeyManagement() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");
  const [newBillingType, setNewBillingType] = useState<"free" | "paid">("paid");
  const [isAdding, setIsAdding] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(SERVICE_OPTIONS[0]!.value);

  // DataForSEO Basic Auth fields
  const [basicAuthLogin, setBasicAuthLogin] = useState("");
  const [basicAuthPassword, setBasicAuthPassword] = useState("");

  const utils = trpc.useUtils();

  const currentService = SERVICE_OPTIONS.find((option) => option.value === selectedService)!;
  const isBasicAuth = SERVICES_WITH_BASIC_AUTH.includes(selectedService);

  // 根據服務類型載入 API Keys
  const { data: apiKeys, isLoading } = trpc.apiKeys.getByService.useQuery({
    serviceType: selectedService,
  });

  const addKeyMutation = trpc.apiKeys.add.useMutation({
    onSuccess: () => {
      toast.success("API Key 已新增");
      setIsAddDialogOpen(false);
      setNewKeyName("");
      setNewKeyValue("");
      setBasicAuthLogin("");
      setBasicAuthPassword("");
      setIsAdding(false);
      utils.apiKeys.getByService.invalidate({ serviceType: selectedService });
    },
    onError: (error) => {
      toast.error(`新增失敗：${error.message}`);
      setIsAdding(false);
    },
  });

  const deleteKeyMutation = trpc.apiKeys.delete.useMutation({
    onSuccess: () => {
      toast.success("API Key 已刪除");
      utils.apiKeys.getByService.invalidate({ serviceType: selectedService });
    },
    onError: (error) => {
      toast.error(`刪除失敗：${error.message}`);
    },
  });

  const handleAddKey = () => {
    // Validation based on auth type
    if (isBasicAuth) {
      if (!newKeyName.trim() || !basicAuthLogin.trim() || !basicAuthPassword.trim()) {
        toast.error("請填寫完整資訊（名稱、Login、Password）");
        return;
      }
    } else {
      if (!newKeyName.trim() || !newKeyValue.trim()) {
        toast.error("請填寫完整資訊");
        return;
      }
    }

    // For Basic Auth, combine login:password
    const keyValue = isBasicAuth
      ? `${basicAuthLogin}:${basicAuthPassword}`
      : newKeyValue;

    setIsAdding(true);
    addKeyMutation.mutate({
      keyName: newKeyName,
      serviceType: selectedService,
      keyValue: keyValue,
      billingType: SERVICES_WITH_FREE_TIER.includes(selectedService) ? newBillingType : "paid",
    });
  };

  const handleDeleteKey = (id: number) => {
    if (confirm("確定要刪除此 API Key 嗎？")) {
      deleteKeyMutation.mutate({ id });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>API Key 管理</CardTitle>
            <CardDescription>
              針對不同供應商維護多組金鑰，系統會自動輪替使用
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="選擇供應商" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  新增 Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>新增 {currentService.label} Key</DialogTitle>
                  <DialogDescription>
                    {currentService.description}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="keyName">Key 名稱</Label>
                    <Input
                      id="keyName"
                      placeholder={`例如：${currentService.label} Key 1`}
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>

                  {isBasicAuth ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="basicLogin">Login (Email)</Label>
                        <Input
                          id="basicLogin"
                          placeholder="your-email@example.com"
                          value={basicAuthLogin}
                          onChange={(e) => setBasicAuthLogin(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="basicPassword">Password (API Password)</Label>
                        <Input
                          id="basicPassword"
                          type="password"
                          placeholder="輸入 API 密碼"
                          value={basicAuthPassword}
                          onChange={(e) => setBasicAuthPassword(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          DataForSEO 使用 Basic Auth，請使用您的登入 Email 與 API 密碼。
                          可在 <a href="https://app.dataforseo.com/api-access" target="_blank" rel="noopener noreferrer" className="text-primary underline">DataForSEO 後台</a> 取得。
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="keyValue">API Key</Label>
                      <Input
                        id="keyValue"
                        type="password"
                        placeholder={`輸入 ${currentService.label} Key`}
                        value={newKeyValue}
                        onChange={(e) => setNewKeyValue(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        金鑰將使用 AES-256 加密儲存
                      </p>
                    </div>
                  )}
                  {SERVICES_WITH_FREE_TIER.includes(selectedService) && (
                    <div className="space-y-2">
                      <Label>帳戶類型</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billingType"
                            value="free"
                            checked={newBillingType === "free"}
                            onChange={() => setNewBillingType("free")}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">免費帳戶</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billingType"
                            value="paid"
                            checked={newBillingType === "paid"}
                            onChange={() => setNewBillingType("paid")}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">付費帳戶</span>
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        系統會優先使用免費帳戶的 Key，付費帳戶作為備援
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddDialogOpen(false)}
                    disabled={isAdding}
                  >
                    取消
                  </Button>
                  <Button onClick={handleAddKey} disabled={isAdding}>
                    {isAdding ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        新增中...
                      </>
                    ) : (
                      "新增"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : apiKeys && apiKeys.length > 0 ? (
          <div className="space-y-3">
            {apiKeys.map((key: any) => (
              <div
                key={key.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1">
                  <Key className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{key.keyName}</p>
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                        {currentService.label}
                      </span>
                      {key.status === "active" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          <Check className="w-3 h-3" />
                          啟用中
                        </span>
                      ) : key.status === "error" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          <X className="w-3 h-3" />
                          錯誤
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                          停用
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">
                      {key.keyValue}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span>使用次數：{key.usageCount}</span>
                      <span>錯誤次數：{key.errorCount}</span>
                      {key.lastUsedAt && (
                        <span>
                          最後使用：
                          {new Date(key.lastUsedAt).toLocaleString("zh-TW")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteKey(key.id)}
                  disabled={deleteKeyMutation.isPending}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              {currentService.label} 尚未新增任何 API Key
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              新增第一個 Key
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
